import { useEffect, useMemo, useState } from "react";
import { FiSearch, FiDownload, FiEye } from "react-icons/fi";
import { message } from "antd";
import LabOrderService from "../../../../Services/LabOrderService";
import IPDOrderService from "../../../../Services/IPDOrderService";

function parseVitalDate(dt) {
  if (!dt) return null;
  const d = new Date(dt.replace(" ", "T"));
  return isNaN(d.getTime()) ? null : d;
}

function formatDate(dt) {
  const d = parseVitalDate(dt);
  if (!d) return "-";
  return d.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function deriveStatus(item) {
  return item?.isLabReportUploaded === "YES" || item?.resultString
    ? "Completed"
    : "Pending";
}

export default function Pharmacy({ patientId, admissionId, patientDetails }) {
  const [labReports, setLabReports] = useState([]);
  const [labReportLoad, setLabReportLoad] = useState(false);
  const [dateFilter, setDateFilter] = useState("");
  const [searchTerm, setSearchTerm] = useState("");

  const patientLabReports = async () => {
    setLabReportLoad(true);
    const payload = {
      status: "1",
      diagnosticType: "MEDICATION",
      ...(patientId ? { patientId } : {}),
    };
    try {
      const response = await LabOrderService.LabReportUploadGet(payload);
      console.log("Pharmacy reports response:", response);
      setLabReports(Array.isArray(response?.data) ? response.data : []);
    } catch (e) {
      console.error("Error fetching pharmacy orders:", e);
      setLabReports([]);
    } finally {
      setLabReportLoad(false);
    }
  };

  useEffect(() => {
    patientLabReports();
  }, [patientId]);
  const handleViewLabReports = (report) => {
    const file = report?.resultDocumentName;
    if (!file) {
      message.error("No file found!!");
      return;
    }
    if (file?.startsWith("http") || file?.startsWith("https")) {
      window.open(file, "_blank");
    } else {
      window.open(import.meta.env.VITE_APP_IMAGE_URL + file, "_blank");
    }
  };

  const handleDownloadLabReport = (report) => {
    const file = report?.resultDocumentName;
    if (!file) {
      message.error("No file found!!");
      return;
    }
    const url =
      file?.startsWith("http") || file?.startsWith("https")
        ? file
        : import.meta.env.VITE_APP_IMAGE_URL + file;

    const link = document.createElement("a");
    link.href = url;
    link.download = file;
    link.target = "_blank";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const filteredReports = useMemo(() => {
    return (labReports || []).filter((item) => {
      if (searchTerm) {
        const term = searchTerm.toLowerCase();
        const matchesName = (item?.labTestName || "")
          .toLowerCase()
          .includes(term);
        const matchesDoctor = (item?.doctor || item?.orderedBy || "")
          .toLowerCase()
          .includes(term);
        if (!matchesName && !matchesDoctor) return false;
      }
      if (dateFilter) {
        const itemDate = formatDate(item?.uploadedDateTime);
        const selDate = formatDate(dateFilter);
        if (
          itemDate !== selDate &&
          !String(item?.uploadedDateTime).includes(dateFilter)
        ) {
          return false;
        }
      }
      return true;
    });
  }, [labReports, searchTerm, dateFilter]);

  return (
    <div className="rounded-lg border bg-white">
      {/* Header */}
      <div className="flex items-center justify-between border-b p-4">
        <h2 className="text-sm font-semibold text-[#3b3f97]">
          Pharmacy orders
        </h2>
        <div className="flex gap-3">
          <input
            type="date"
            value={dateFilter}
            onChange={(e) => setDateFilter(e.target.value)}
            className="h-8 rounded-md border border-[#e5e7eb] bg-white px-4 text-[13px] font-semibold text-[#111827] outline-none focus:border-[#5b5bd6]"
          />
          <div className="relative">
            <FiSearch className="absolute left-3 top-2.5 text-gray-400" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search..."
              className="rounded-lg border py-2 pl-9 pr-3 text-xs outline-none"
            />
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="min-w-full text-xs">
          <thead className="bg-gray-50 text-gray-700">
            <tr>
              {/* <th className="p-3 text-left">Report ID</th> */}
              <th className="p-3 text-left">Order Name</th>
              {/* <th className="p-3 text-left">Sample Date</th> */}
              <th className="p-3 text-left"> Date</th>
              {/* <th className="p-3 text-left">Doctor</th> */}
              <th className="p-3 text-left">Status</th>
              <th className="p-3 text-center">Action</th>
            </tr>
          </thead>

          <tbody>
            {labReportLoad ? (
              <tr>
                <td colSpan={7} className="p-6 text-center text-gray-400">
                  Loading pharmacy orders...
                </td>
              </tr>
            ) : filteredReports.length === 0 ? (
              <tr>
                <td colSpan={7} className="p-6 text-center text-gray-400">
                  No pharmacy orders found.
                </td>
              </tr>
            ) : (
              filteredReports.map((item) => {
                const status = deriveStatus(item);
                return (
                  <tr key={item.id} className="border-t hover:bg-gray-50">
                    {/* <td className="p-3">{item.orderedBy || "-"}</td> */}
                    <td className="p-3 font-medium">
                      {item.labTestName || "-"}
                    </td>
                    {/* <td className="p-3">
                      {item.sampleDate ? formatDate(item.sampleDate) : "-"}
                    </td> */}
                    <td className="p-3">
                      {item.uploadedDateTime
                        ? formatDate(item.uploadedDateTime)
                        : "-"}
                    </td>
                    {/* <td className="p-3">{item.doctor || "-"}</td> */}

                    <td className="p-3">
                      <span
                        className={`rounded px-2 py-1 text-[10px] ${
                          status === "Completed"
                            ? "bg-green-100 text-green-700"
                            : "bg-orange-100 text-orange-700"
                        }`}
                      >
                        {status}
                      </span>
                    </td>

                    <td className="p-3">
                      <div className="flex gap-3 justify-center">
                        <button
                          onClick={() => handleViewLabReports(item)}
                          className="rounded border bg-white p-2 text-blue-600 hover:bg-blue-50"
                        >
                          <FiEye size={14} />
                        </button>
                        {/* <button
                          onClick={() => handleDownloadLabReport(item)}
                          className="rounded border p-2 bg-white text-blue-600 hover:bg-blue-50"
                        >
                          <FiDownload size={14} />
                        </button> */}
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
